import { Pledge } from '#library/pledge.class.js';
import { decodeJWT } from '#library/webtoken.library.js';
import { secure } from '#library/proxy.library.js';
import { asError } from '#library/coercion.library.js';
import { isObject, isNumber, isFunction } from '#library/assertion.library.js';
import { isSyncToken } from '../../support/support.util.js';
import { logWarn, logDebug } from '../../support/support.util.js';
import { LICENSE } from '../../support/support.enum.js';
import { getRuntime } from '../../support/support.runtime.js';
let stateLicenseCache = new WeakMap();
const logMessage = 'Tempo Licensing:';
export function getLicenseState(state) {
    const runtime = getRuntime();
    if (state === runtime.state)
        return runtime.license;
    return stateLicenseCache.get(state) ?? runtime.license;
}
export function ensureLicenseState(state) {
    let license = stateLicenseCache.get(state);
    if (!license) {
        license = { status: LICENSE.None, scopes: {} };
        stateLicenseCache.set(state, license);
        if (state !== getRuntime().state)
            state.license = license;
    }
    return license;
}
export function getLicenseSnapshot(state) {
    const license = getLicenseState(state);
    const snapshot = {
        ...license,
        scopes: isObject(license.scopes) ? { ...license.scopes } : {},
    };
    delete snapshot.jws;
    return secure(snapshot);
}
export function updateScopeStatus(state, scopeKey, status, error) {
    const license = getLicenseState(state);
    if (!license || !license.scopes)
        return;
    const scopes = { ...license.scopes };
    if (scopes[scopeKey]) {
        scopes[scopeKey] = {
            ...scopes[scopeKey],
            status,
            ...(error ? { error } : {})
        };
    }
    else {
        scopes[scopeKey] = {
            status,
            ...(error ? { error } : {})
        };
    }
    license.scopes = scopes;
    const activeScopes = Object.values(scopes).filter((s) => s && s.status !== LICENSE.Revoked && s.status !== 'revoked');
    if (activeScopes.length === 0 && Object.keys(scopes).length > 0) {
        license.status = LICENSE.Revoked;
        if (error)
            license.error = error;
    }
}
function disposePendingLicense(license) {
    const jws = license?.jws;
    if (jws && jws.isPending) {
        if (jws.promise && isFunction(jws.promise.catch))
            jws.promise.catch(() => { });
        // Do not explicitly reject stale license promises during teardown.
        // The stale pledge already has onReject/onResolve handlers and state
        // guards that will ignore later results based on JTI/key mismatch.
    }
}
export function setLicense(state, key) {
    const runtime = getRuntime();
    const license = (state === runtime.state ? runtime.license : ensureLicenseState(state));
    if (key) {
        disposePendingLicense(license);
        const claims = decodeJWT(key);
        license.key = key;
        license.status = LICENSE.Pending;
        license.scopes = claims?.scopes || {};
        if (claims?.role)
            license.role = claims.role;
        else
            delete license.role;
        if (claims?.exp)
            license.expires = claims.exp;
        if (claims?.iat)
            license.issuedAt = claims.iat;
        if (claims?.iss)
            license.issuer = claims.iss;
        if (claims?.sub)
            license.subject = claims.sub;
        if (claims?.aud)
            license.audience = claims.aud;
        if (claims?.jti)
            license.jti = claims.jti;
        delete license.error;
        const initialJti = license.jti;
        const initialKey = license.key;
        const argObj = {
            tag: 'license',
            onResolve: (res) => {
                if (license.jti !== initialJti || license.key !== initialKey)
                    return;
                const isValidStatus = isSyncToken(res.status) || LICENSE.values().includes(res.status);
                license.status = isValidStatus ? res.status : LICENSE.Invalid;
                license.scopes = isObject(res.scopes) ? res.scopes : {};
                if (res.role)
                    license.role = res.role;
                else
                    delete license.role;
                delete license.error;
                if (res.error)
                    license.error = res.error;
                if (res.expires)
                    license.expires = res.expires;
                if (res.issuedAt)
                    license.issuedAt = res.issuedAt;
                if (res.issuer)
                    license.issuer = res.issuer;
                if (res.jti)
                    license.jti = res.jti;
                if ([LICENSE.Revoked, LICENSE.Invalid].includes(res.status))
                    logWarn(`⚠️ ${logMessage} ${res.error || 'Verification failed'}`, state.config);
                else
                    warnIfExpiringSoon(license, state.config);
                const scopesList = Object.values(license.scopes || {});
                const shouldSkipRevocation = scopesList.length > 0 && scopesList.every((s) => s?.skipRevocationCheck === true);
                if (!shouldSkipRevocation && (res.revocationPromise || res.getRevocationPromise)) {
                    const promise = typeof res.getRevocationPromise === 'function'
                        ? res.getRevocationPromise()
                        : typeof res.revocationPromise === 'function'
                            ? res.revocationPromise()
                            : res.revocationPromise;
                    if (promise && typeof promise.then === 'function') {
                        promise.then((isRevoked) => {
                            if (isRevoked && license.jti === initialJti && license.key === initialKey) {
                                license.status = LICENSE.Revoked;
                                license.error = 'License has been revoked by the issuer.';
                                logWarn(`⚠️ ${logMessage} ${license.error}`, state.config);
                            }
                            else {
                                warnIfExpiringSoon(license, state.config);
                            }
                        }).catch((err) => {
                            const { message } = asError(err);
                            logDebug(`${logMessage} Background revocation check failed for JTI ${initialJti} - ${message}`, state.config);
                        });
                    }
                }
            },
            onReject: (err) => {
                if (license.jti !== initialJti || license.key !== initialKey)
                    return;
                const error = asError(err);
                license.status = LICENSE.Invalid;
                license.error = error.message || 'Verification rejected';
                logWarn(`⚠️ ${logMessage} ${license.error}`, state.config);
            }
        };
        license.jws = new Pledge(argObj);
    }
}
export function warnIfExpiringSoon(license, config) {
    const nowS = Math.floor(Date.now() / 1_000);
    const checkExpiry = (label, expS, iatS) => {
        if (!expS)
            return;
        const remainingS = expS - nowS;
        if (remainingS <= 0)
            return;
        const windowS = iatS
            ? (expS - iatS) * 0.25
            : 30 * 24 * 60 * 60;
        if (remainingS <= windowS) {
            const days = Math.ceil(remainingS / 86_400);
            const plural = days === 1 ? 'day' : 'days';
            logWarn(`⏳ ${logMessage} ${label} expires in ${days} ${plural} — visit https://registry.magmacomputing.com.au to renew.`, config);
        }
    };
    checkExpiry('License key', isNumber(license.expires) ? license.expires : undefined, license.issuedAt);
    for (const [scopeKey, meta] of Object.entries(license.scopes || {})) {
        const m = meta;
        if (m?.exp)
            checkExpiry(`Scope '${scopeKey}'`, m.exp, m.iat ?? license.issuedAt);
    }
}
export function validateLicenseState(license, jws) {
    const runtime = getRuntime();
    import('#tempo/license')
        .then(m => new m.Validator(license.key, runtime.state?.config).verify())
        .then(res => jws.resolve(res))
        .catch(err => {
        if (license.jws === jws)
            license.status = LICENSE.None;
        jws.reject(err);
    });
}
